import { Heading } from "@medoma/components/Heading/Heading";
import styles from "./DailyOverview.module.scss";
import { Trans, useLingui } from "@lingui/react/macro";
import { SplitPane } from "@/components/SplitPane/SplitPane";
import { Outlet, useNavigate } from "react-router";
import { ProspectPatientsList } from "./ProspectPatientsList";
import { AdmittedPatientsList } from "./AdmittedPatientsList";
import { PausedPatientsList } from "./PausedPatientsList";
import { PageToolbar } from "@/components/PageToolbar/PageToolbar";
import { Tab, TabList } from "@medoma/components/Tabs";
import { useState } from "react";
import { DailyOverviewContextProvider } from "./DailyOverviewContextComponents";
import { DailyOverviewPatientNameFilterInput } from "./DailyOverviewPatientNameFilterInput";
import { DailyOverviewFilterBannerSection } from "./DailyOverviewFilterBannerSection";
import { z } from "zod";
import { DaySwitcher } from "@/components/DaySwitcher/DaySwitcher";
import { BackButton } from "@/components/NavigationButtons/NavigationButtons";

const patientGroupSchema = z.enum(["prospect", "admitted", "paused"]);
type PatientGroup = z.infer<typeof patientGroupSchema>;

const PatientsDailyOverview = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [patientGroup, setPatientGroup] = useState<PatientGroup>(
    patientGroupSchema.enum.admitted,
  );

  return (
    <DailyOverviewContextProvider>
      <PageToolbar showDaySwitcher={false}>
        <div className={styles.pageHeader}>
          <BackButton
            navigateBack={() => navigate("..")}
            ariaLabel={t`Tillbaka till patienter`}
          />
          <Heading level="h1" size="h2">
            <Trans>Dagsöversikt</Trans>
          </Heading>
          <DaySwitcher size="h2" />
        </div>
        <div className={styles.tools}>
          <DailyOverviewPatientNameFilterInput />
        </div>
      </PageToolbar>
      <DailyOverviewFilterBannerSection />
      <div className={styles.tabs}>
        <TabList stateOwner="react">
          <Tab
            onClick={() => setPatientGroup(patientGroupSchema.enum.prospect)}
            isSelected={patientGroup === patientGroupSchema.enum.prospect}
          >
            <Trans>Under bedömning</Trans>
          </Tab>
          <Tab
            onClick={() => setPatientGroup(patientGroupSchema.enum.admitted)}
            isSelected={patientGroup === patientGroupSchema.enum.admitted}
          >
            <Trans>Inskrivna</Trans>
          </Tab>
          <Tab
            onClick={() => setPatientGroup(patientGroupSchema.enum.paused)}
            isSelected={patientGroup === patientGroupSchema.enum.paused}
          >
            <Trans>Pausade</Trans>
          </Tab>
        </TabList>
      </div>
      <div className={styles.dailyOverview}>
        <SplitPane panelSizeLocalStorageKey="patients-daily-overview">
          {patientGroup === patientGroupSchema.enum.prospect ? (
            <ProspectPatientsList />
          ) : patientGroup === patientGroupSchema.enum.paused ? (
            <PausedPatientsList />
          ) : (
            <AdmittedPatientsList />
          )}
          <Outlet />
        </SplitPane>
      </div>
    </DailyOverviewContextProvider>
  );
};

export { PatientsDailyOverview };
